import { AlertCircle, Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import SEO from "@/components/site/SEO";
import SectionRenderer from "@/components/sections/SectionRenderer";
import { getIndustry, getService } from "@/lib/api";
import { useAsync } from "@/lib/useAsync";
import { buildBreadcrumbs } from "@/lib/breadcrumbs";

/**
 * Generic renderer for a `services` or `industries` document.
 * Same pattern as CMSPage but the fetcher is chosen via `kind`.
 */
export default function CollectionDetailPage({ kind, slug }) {
  const fetcher = kind === "service" ? getService : getIndustry;
  const { data, loading, error } = useAsync(() => fetcher(slug), [kind, slug]);
  const { pathname } = useLocation();
  const breadcrumbs = buildBreadcrumbs(pathname);

  if (loading) {
    return (
      <div
        data-testid={`${kind}-${slug}-loading`}
        className="min-h-[60vh] flex items-center justify-center bg-mist-off"
      >
        <Loader2 className="h-8 w-8 text-mist-navy animate-spin" aria-hidden />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        data-testid={`${kind}-${slug}-error`}
        className="min-h-[60vh] flex flex-col items-center justify-center gap-3 bg-mist-off text-mist-ink"
      >
        <AlertCircle className="h-8 w-8 text-mist-navy" aria-hidden />
        <div className="text-lg font-display text-mist-navy">Content unavailable</div>
        <div className="text-sm text-mist-ink/70">
          We could not load this page. Please try again shortly.
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO seo={data.seo} fallbackTitle={data.title} />
      <div data-testid={`${kind}-${slug}`}>
        <SectionRenderer sections={data.sections || []} banner="page" breadcrumbs={breadcrumbs} />
      </div>
    </>
  );
}
